import React, { useState, useEffect } from "react";
import api from "@/utils/api";
import { useAuth } from "@/context/AuthContext";
import { DollarSign, FileText, Plus, CheckCircle, X, CreditCard, Receipt, TrendingUp, Calendar, Building } from "lucide-react";
import { toast } from "sonner";

// WCC Types for creation
const WCC_TYPES = [
  { value: "survey_ci", label: "Survey CI" },
  { value: "installation_mi", label: "Installation MI" },
  { value: "service_cable", label: "Service Cable" },
];

export default function FinancePage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState("wcc");
  const [wccs, setWCCs] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [payments, setPayments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [circles, setCircles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showWCCModal, setShowWCCModal] = useState(false);
  const [showInvoiceModal, setShowInvoiceModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [selectedWCC, setSelectedWCC] = useState(null);
  const [selectedInvoice, setSelectedInvoice] = useState(null);

  const canCreateWCC = ["admin", "ci", "finance"].includes(user?.role);
  const canCreateInvoice = ["admin", "finance"].includes(user?.role);
  const canRecordPayment = ["admin", "finance"].includes(user?.role);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [wccRes, invRes, payRes, sumRes, circleRes] = await Promise.all([
        api.get("/wcc", { params: { limit: 50 } }),
        api.get("/invoices", { params: { limit: 50 } }),
        api.get("/payments", { params: { limit: 50 } }),
        api.get("/summary"),
        api.get("/auth/circles")
      ]);
      setWCCs(wccRes.data.items || []);
      setInvoices(invRes.data.items || []);
      setPayments(payRes.data.items || []);
      setSummary(sumRes.data);
      setCircles(circleRes.data || []);
    } catch (e) {
      console.error("Failed to load finance data:", e);
      toast.error("Failed to load finance data");
    }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchData(); }, []);

  const handleApproveWCC = async (id) => {
    try {
      await api.put(`/wcc/${id}/approve`);
      toast.success("WCC approved");
      fetchData();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const handleCreateInvoice = (wcc) => {
    setSelectedWCC(wcc);
    setShowInvoiceModal(true);
  };

  const handleRecordPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setShowPaymentModal(true);
  };

  const totalBilled = invoices.reduce((sum, inv) => sum + (inv.total_amount || 0), 0);
  const totalPending = invoices.reduce((sum, inv) => sum + (inv.balance_due || 0), 0);
  const totalReceived = summary?.total_payments_received || 0;

  return (
    <div className="p-4 md:p-6 animate-fade-up" data-testid="finance-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-black text-slate-900" style={{ fontFamily: "Manrope" }}>Finance</h1>
          <p className="text-slate-500 text-sm">WCC · Invoices · Payments</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {canCreateWCC && (
            <button
              onClick={() => setShowWCCModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-orange-500 text-white rounded-sm text-sm font-bold hover:bg-orange-600"
              data-testid="create-wcc-btn"
            >
              <Plus size={16} />
              Generate WCC
            </button>
          )}
          {canCreateInvoice && (
            <button
              onClick={() => {
                setSelectedWCC(null);
                setShowInvoiceModal(true);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-blue-500 text-white rounded-sm text-sm font-bold hover:bg-blue-600"
              data-testid="create-invoice-btn"
            >
              <Receipt size={16} />
              Create Invoice
            </button>
          )}
        </div>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
        <div className="metric-card-primary">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total WCCs</p>
          <p className="text-3xl font-black text-slate-900">{wccs.length}</p>
        </div>
        <div className="metric-card-primary">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Invoices</p>
          <p className="text-3xl font-black text-slate-900">{invoices.length}</p>
        </div>
        <div className="metric-card-primary">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Total Billed</p>
          <p className="text-2xl font-black text-green-600">₹{(totalBilled / 100000).toFixed(1)}L</p>
        </div>
        <div className="metric-card-primary">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Received</p>
          <p className="text-2xl font-black text-blue-600">₹{(totalReceived / 100000).toFixed(1)}L</p>
        </div>
        <div className="metric-card-primary">
          <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider mb-1">Balance Due</p>
          <p className="text-2xl font-black text-orange-600">₹{(totalPending / 100000).toFixed(1)}L</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 bg-slate-100 rounded-sm p-1 w-fit">
        {[
          { id: "wcc", label: "WCC", icon: FileText },
          { id: "invoices", label: "Invoices", icon: Receipt },
          { id: "payments", label: "Payments", icon: CreditCard },
        ].map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`flex items-center gap-1.5 px-4 py-1.5 rounded-sm text-xs font-semibold transition-colors ${activeTab === tab.id ? "bg-white text-orange-600 shadow-sm" : "text-slate-500 hover:text-slate-700"}`}
            data-testid={`finance-tab-${tab.id}`}>
            <tab.icon size={14} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* WCC Tab */}
      {activeTab === "wcc" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead>
              <tr>
                <th>WCC Number</th>
                <th>Type</th>
                <th>Month</th>
                <th>Circle</th>
                <th>DTs</th>
                <th>Amount</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? [...Array(5)].map((_, i) => <tr key={i}>{[...Array(8)].map((_, j) => <td key={j}><div className="h-4 bg-slate-100 rounded animate-pulse"></div></td>)}</tr>)
                : wccs.length === 0 ? (
                  <tr><td colSpan={8} className="text-center py-12 text-slate-400"><FileText size={32} className="mx-auto mb-2" />No WCC records{canCreateWCC && <p className="mt-2 text-sm">Click "Generate WCC" to create one</p>}</td></tr>
                ) : wccs.map(wcc => (
                  <tr key={wcc.id} data-testid={`wcc-row-${wcc.id}`}>
                    <td><span className="font-mono text-xs font-bold text-orange-600">{wcc.wcc_number}</span></td>
                    <td><span className="text-xs text-slate-600 capitalize">{wcc.wcc_type?.replace("_", " ")}</span></td>
                    <td className="text-xs text-slate-600">{wcc.billing_month}</td>
                    <td className="text-xs text-slate-600">{wcc.circle_name}</td>
                    <td className="text-xs font-semibold">{wcc.total_dts}</td>
                    <td className="text-xs font-bold text-green-600">₹{wcc.total_amount?.toLocaleString("en-IN")}</td>
                    <td>
                      <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                        wcc.status === "approved" ? "bg-green-100 text-green-700" : 
                        wcc.status === "draft" ? "bg-orange-100 text-orange-700" : "bg-slate-100 text-slate-600"
                      }`}>{wcc.status}</span>
                    </td>
                    <td className="space-x-2">
                      {wcc.status === "draft" && ["admin", "ci"].includes(user?.role) && (
                        <button 
                          onClick={() => handleApproveWCC(wcc.id)} 
                          className="text-xs text-green-600 font-bold hover:text-green-700" 
                          data-testid={`approve-wcc-${wcc.id}`}
                        >
                          Approve
                        </button>
                      )}
                      {wcc.status === "approved" && canCreateInvoice && (
                        <button 
                          onClick={() => handleCreateInvoice(wcc)} 
                          className="text-xs text-blue-600 font-bold hover:text-blue-700"
                          data-testid={`invoice-wcc-${wcc.id}`}
                        >
                          Create Invoice
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Invoices Tab */}
      {activeTab === "invoices" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead>
              <tr>
                <th>Invoice #</th>
                <th>Date</th>
                <th>Taxable</th>
                <th>GST</th>
                <th>TDS</th>
                <th>Total</th>
                <th>Balance</th>
                <th>Status</th>
                {canRecordPayment && <th>Action</th>}
              </tr>
            </thead>
            <tbody>
              {invoices.length === 0 ? (
                <tr><td colSpan={9} className="text-center py-12 text-slate-400"><Receipt size={32} className="mx-auto mb-2" />No invoices found</td></tr>
              ) : invoices.map(inv => (
                <tr key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                  <td><span className="font-mono text-xs font-bold text-blue-600">{inv.invoice_number}</span></td>
                  <td className="text-xs text-slate-600">{inv.invoice_date}</td>
                  <td className="text-xs">₹{inv.taxable_amount?.toLocaleString("en-IN")}</td>
                  <td className="text-xs text-green-600">+₹{inv.gst_amount?.toLocaleString("en-IN")}</td>
                  <td className="text-xs text-red-600">-₹{inv.tds_amount?.toLocaleString("en-IN")}</td>
                  <td className="text-xs font-bold text-slate-900">₹{inv.total_amount?.toLocaleString("en-IN")}</td>
                  <td className="text-xs font-bold text-orange-600">₹{inv.balance_due?.toLocaleString("en-IN")}</td>
                  <td>
                    <span className={`px-2 py-0.5 rounded text-[10px] font-bold uppercase ${
                      inv.status === "paid" ? "bg-green-100 text-green-700" : 
                      inv.status === "partial" ? "bg-yellow-100 text-yellow-700" : "bg-orange-100 text-orange-700"
                    }`}>{inv.status}</span>
                  </td>
                  {canRecordPayment && (
                    <td>
                      {inv.balance_due > 0 && (
                        <button 
                          onClick={() => handleRecordPayment(inv)} 
                          className="text-xs text-green-600 font-bold hover:text-green-700"
                          data-testid={`pay-invoice-${inv.id}`}
                        >
                          Record Payment
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Payments Tab */}
      {activeTab === "payments" && (
        <div className="bg-white border border-slate-200 rounded-sm overflow-hidden shadow-sm">
          <table className="dt-table">
            <thead>
              <tr>
                <th>Date</th>
                <th>Invoice</th>
                <th>Amount</th>
                <th>Mode</th>
                <th>Reference</th>
              </tr>
            </thead>
            <tbody>
              {payments.length === 0 ? (
                <tr><td colSpan={5} className="text-center py-12 text-slate-400"><CreditCard size={32} className="mx-auto mb-2" />No payments recorded</td></tr>
              ) : payments.map(pay => (
                <tr key={pay.id} data-testid={`payment-row-${pay.id}`}>
                  <td className="text-xs text-slate-600">{pay.payment_date}</td>
                  <td><span className="font-mono text-xs text-blue-600">{pay.invoice_id?.slice(-8)}</span></td>
                  <td className="text-sm font-bold text-green-600">₹{pay.amount?.toLocaleString("en-IN")}</td>
                  <td className="text-xs text-slate-600 capitalize">{pay.payment_mode}</td>
                  <td className="text-xs text-slate-500">{pay.payment_reference || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* WCC Creation Modal */}
      {showWCCModal && (
        <WCCCreationModal
          circles={circles}
          userCircle={user?.circle_name}
          userRole={user?.role}
          onClose={() => setShowWCCModal(false)}
          onSuccess={() => {
            setShowWCCModal(false);
            fetchData();
          }}
        />
      )}

      {/* Invoice Creation Modal */}
      {showInvoiceModal && (
        <InvoiceCreationModal
          wcc={selectedWCC}
          wccs={wccs.filter(w => w.status === "approved")}
          onClose={() => {
            setShowInvoiceModal(false);
            setSelectedWCC(null);
          }}
          onSuccess={() => {
            setShowInvoiceModal(false);
            setSelectedWCC(null);
            fetchData();
          }}
        />
      )}

      {/* Payment Recording Modal */}
      {showPaymentModal && selectedInvoice && (
        <PaymentModal
          invoice={selectedInvoice}
          onClose={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
          }}
          onSuccess={() => {
            setShowPaymentModal(false);
            setSelectedInvoice(null);
            fetchData();
          }}
        />
      )}
    </div>
  );
}

// =============================================================================
// WCC CREATION MODAL
// =============================================================================
function WCCCreationModal({ circles, userCircle, userRole, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    wcc_type: "installation_mi",
    billing_month: new Date().toISOString().slice(0, 7),
    circle_name: userRole === "ci" ? userCircle : "",
  });
  const [eligibleDTs, setEligibleDTs] = useState([]);
  const [selectedDTs, setSelectedDTs] = useState([]);
  const [fetchingDTs, setFetchingDTs] = useState(false);

  const fetchEligibleDTs = async () => {
    if (!form.circle_name) return;
    setFetchingDTs(true);
    try {
      const res = await api.get("/dt-master", { 
        params: { 
          lifecycle_status: "qc_passed", 
          circle_name: form.circle_name,
          limit: 200 
        } 
      });
      setEligibleDTs(res.data.items || []);
    } catch (e) {
      toast.error("Failed to fetch eligible DTs");
    } finally {
      setFetchingDTs(false);
    }
  };

  useEffect(() => {
    if (form.circle_name) {
      fetchEligibleDTs();
      setSelectedDTs([]);
    }
  }, [form.circle_name]);

  const toggleDT = (dtId) => {
    if (selectedDTs.includes(dtId)) {
      setSelectedDTs(selectedDTs.filter(id => id !== dtId));
    } else {
      setSelectedDTs([...selectedDTs, dtId]);
    }
  };

  const selectAll = () => {
    if (selectedDTs.length === eligibleDTs.length) {
      setSelectedDTs([]);
    } else {
      setSelectedDTs(eligibleDTs.map(dt => dt.id));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (selectedDTs.length === 0) {
      toast.error("Please select at least one DT");
      return;
    }

    setLoading(true);
    try {
      await api.post("/wcc", {
        wcc_type: form.wcc_type,
        billing_month: form.billing_month,
        circle_name: form.circle_name,
        dt_ids: selectedDTs,
      });
      toast.success("WCC generated successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate WCC");
    } finally {
      setLoading(false);
    }
  };

  const estimatedAmount = selectedDTs.length * 2500;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-start justify-center z-[100] p-4 overflow-y-auto">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-2xl my-8 animate-fade-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-orange-500 to-orange-600">
          <div>
            <h2 className="text-lg font-bold text-white">Generate WCC</h2>
            <p className="text-orange-100 text-xs">Work Completion Certificate for QC Passed DTs</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="field-label">WCC Type *</label>
              <select
                className="field-select"
                value={form.wcc_type}
                onChange={e => setForm({ ...form, wcc_type: e.target.value })}
                data-testid="wcc-type"
              >
                {WCC_TYPES.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div>
              <label className="field-label">Billing Month *</label>
              <input
                type="month"
                className="field-input"
                value={form.billing_month}
                onChange={e => setForm({ ...form, billing_month: e.target.value })}
                data-testid="wcc-month"
              />
            </div>
            <div>
              <label className="field-label">Circle *</label>
              <select
                className="field-select"
                value={form.circle_name}
                onChange={e => setForm({ ...form, circle_name: e.target.value })}
                disabled={userRole === "ci"}
                data-testid="wcc-circle"
              >
                <option value="">Select Circle</option>
                {circles.map(c => (
                  <option key={c.name} value={c.name}>{c.name}</option>
                ))}
              </select>
            </div>
          </div>

          {/* DT Selection */}
          <div className="border border-slate-200 rounded-sm overflow-hidden">
            <div className="bg-slate-100 px-4 py-3 flex items-center justify-between">
              <h3 className="font-bold text-slate-700 text-sm">
                QC Passed DTs ({selectedDTs.length}/{eligibleDTs.length} selected)
              </h3>
              {eligibleDTs.length > 0 && (
                <button
                  type="button"
                  onClick={selectAll}
                  className="text-xs text-orange-600 font-bold hover:text-orange-700"
                >
                  {selectedDTs.length === eligibleDTs.length ? "Deselect All" : "Select All"}
                </button>
              )}
            </div>
            <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
              {!form.circle_name ? (
                <div className="p-8 text-center text-slate-400">
                  <Building size={32} className="mx-auto mb-2 opacity-50" />
                  <p>Select a circle first</p>
                </div>
              ) : fetchingDTs ? (
                <div className="p-8 text-center text-slate-400">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500 mx-auto mb-2"></div>
                  <p>Loading eligible DTs...</p>
                </div>
              ) : eligibleDTs.length === 0 ? (
                <div className="p-8 text-center text-slate-400">
                  <FileText size={32} className="mx-auto mb-2 opacity-50" />
                  <p>No QC Passed DTs found in {form.circle_name}</p>
                </div>
              ) : eligibleDTs.map(dt => (
                <div 
                  key={dt.id} 
                  onClick={() => toggleDT(dt.id)}
                  className={`p-3 flex items-center gap-3 cursor-pointer ${selectedDTs.includes(dt.id) ? "bg-orange-50" : "hover:bg-slate-50"}`}
                >
                  <div className={`w-5 h-5 rounded border-2 flex items-center justify-center ${
                    selectedDTs.includes(dt.id) ? "bg-orange-500 border-orange-500 text-white" : "border-slate-300"
                  }`}>
                    {selectedDTs.includes(dt.id) && <CheckCircle size={12} />}
                  </div>
                  <div className="flex-1">
                    <span className="font-mono text-sm font-semibold">{dt.dt_code}</span>
                    <span className="text-xs text-slate-500 ml-2">{dt.feeder_name}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Summary */}
          <div className="bg-gradient-to-r from-slate-800 to-slate-900 rounded-sm p-4 text-white">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-slate-400 text-xs uppercase">WCC Estimate</div>
                <div className="flex items-center gap-4 mt-1">
                  <div>
                    <span className="text-slate-400 text-sm">DTs: </span>
                    <span className="text-2xl font-black text-orange-400">{selectedDTs.length}</span>
                  </div>
                  <div>
                    <span className="text-slate-400 text-sm">Amount: </span>
                    <span className="text-2xl font-black text-green-400">₹{estimatedAmount.toLocaleString("en-IN")}</span>
                  </div>
                </div>
              </div>
              <div className="flex gap-3">
                <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-600 text-slate-300 rounded-sm font-medium hover:bg-slate-700">
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={loading || selectedDTs.length === 0}
                  className="px-5 py-2 bg-orange-500 text-white rounded-sm font-bold hover:bg-orange-600 disabled:opacity-60"
                  data-testid="submit-wcc"
                >
                  {loading ? "Generating..." : "Generate WCC"}
                </button>
              </div>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// INVOICE CREATION MODAL
// =============================================================================
function InvoiceCreationModal({ wcc, wccs, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    wcc_id: wcc?.id || "",
    taxable_amount: wcc?.total_amount || 0,
    gst_pct: 18,
    tds_pct: 2,
  });

  const selectedWCC = wccs.find(w => w.id === form.wcc_id);
  const gstAmount = form.taxable_amount * form.gst_pct / 100;
  const tdsAmount = form.taxable_amount * form.tds_pct / 100;
  const totalAmount = form.taxable_amount + gstAmount - tdsAmount;

  useEffect(() => {
    if (form.wcc_id) {
      const w = wccs.find(wc => wc.id === form.wcc_id);
      if (w) {
        setForm(f => ({ ...f, taxable_amount: w.total_amount || 0 }));
      }
    }
  }, [form.wcc_id, wccs]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.wcc_id) {
      toast.error("Please select a WCC");
      return;
    }

    setLoading(true);
    try {
      await api.post("/invoices", {
        wcc_id: form.wcc_id,
        taxable_amount: form.taxable_amount,
        gst_pct: form.gst_pct,
        tds_pct: form.tds_pct,
      });
      toast.success("Invoice created successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create invoice");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-lg animate-fade-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-blue-500 to-blue-600">
          <div>
            <h2 className="text-lg font-bold text-white">Create Invoice</h2>
            <p className="text-blue-100 text-xs">Generate invoice from approved WCC</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="field-label">Select WCC *</label>
            <select
              className="field-select"
              value={form.wcc_id}
              onChange={e => setForm({ ...form, wcc_id: e.target.value })}
              data-testid="invoice-wcc"
            >
              <option value="">Select Approved WCC</option>
              {wccs.map(w => (
                <option key={w.id} value={w.id}>
                  {w.wcc_number} - {w.circle_name} - ₹{w.total_amount?.toLocaleString("en-IN")}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="field-label">Taxable Amount</label>
              <input
                type="number"
                className="field-input"
                value={form.taxable_amount}
                onChange={e => setForm({ ...form, taxable_amount: parseFloat(e.target.value) || 0 })}
                data-testid="invoice-amount"
              />
            </div>
            <div>
              <label className="field-label">GST %</label>
              <input
                type="number"
                className="field-input"
                value={form.gst_pct}
                onChange={e => setForm({ ...form, gst_pct: parseFloat(e.target.value) || 0 })}
              />
            </div>
            <div>
              <label className="field-label">TDS %</label>
              <input
                type="number"
                className="field-input"
                value={form.tds_pct}
                onChange={e => setForm({ ...form, tds_pct: parseFloat(e.target.value) || 0 })}
              />
            </div>
          </div>

          {/* Calculation Summary */}
          <div className="bg-slate-50 rounded-sm p-4 space-y-2 text-sm">
            <div className="flex justify-between">
              <span className="text-slate-600">Taxable Amount</span>
              <span className="font-semibold">₹{form.taxable_amount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-green-600">
              <span>+ GST ({form.gst_pct}%)</span>
              <span className="font-semibold">₹{gstAmount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-red-600">
              <span>- TDS ({form.tds_pct}%)</span>
              <span className="font-semibold">₹{tdsAmount.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between border-t border-slate-200 pt-2 text-lg font-bold">
              <span>Total Invoice Amount</span>
              <span className="text-blue-600">₹{totalAmount.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-300 rounded-sm font-medium hover:bg-slate-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || !form.wcc_id}
              className="px-5 py-2 bg-blue-500 text-white rounded-sm font-bold hover:bg-blue-600 disabled:opacity-60"
              data-testid="submit-invoice"
            >
              {loading ? "Creating..." : "Create Invoice"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// =============================================================================
// PAYMENT RECORDING MODAL
// =============================================================================
function PaymentModal({ invoice, onClose, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    amount: invoice.balance_due || 0,
    payment_mode: "bank_transfer",
    payment_reference: "",
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (form.amount <= 0) {
      toast.error("Please enter a valid amount");
      return;
    }

    setLoading(true);
    try {
      await api.post("/payments", {
        invoice_id: invoice.id,
        amount: form.amount,
        payment_mode: form.payment_mode,
        payment_reference: form.payment_reference,
      });
      toast.success("Payment recorded successfully!");
      onSuccess();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to record payment");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[100] p-4">
      <div className="bg-white rounded-sm shadow-2xl w-full max-w-md animate-fade-up">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 bg-gradient-to-r from-green-500 to-green-600">
          <div>
            <h2 className="text-lg font-bold text-white">Record Payment</h2>
            <p className="text-green-100 text-xs">Invoice: {invoice.invoice_number}</p>
          </div>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={24} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div className="bg-slate-50 rounded-sm p-3 text-sm">
            <div className="flex justify-between mb-1">
              <span className="text-slate-600">Invoice Total</span>
              <span className="font-semibold">₹{invoice.total_amount?.toLocaleString("en-IN")}</span>
            </div>
            <div className="flex justify-between text-orange-600">
              <span>Balance Due</span>
              <span className="font-bold">₹{invoice.balance_due?.toLocaleString("en-IN")}</span>
            </div>
          </div>

          <div>
            <label className="field-label">Payment Amount *</label>
            <input
              type="number"
              className="field-input"
              value={form.amount}
              onChange={e => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })}
              max={invoice.balance_due}
              data-testid="payment-amount"
            />
          </div>

          <div>
            <label className="field-label">Payment Mode *</label>
            <select
              className="field-select"
              value={form.payment_mode}
              onChange={e => setForm({ ...form, payment_mode: e.target.value })}
              data-testid="payment-mode"
            >
              <option value="bank_transfer">Bank Transfer</option>
              <option value="cheque">Cheque</option>
              <option value="upi">UPI</option>
              <option value="cash">Cash</option>
            </select>
          </div>

          <div>
            <label className="field-label">Reference Number</label>
            <input
              type="text"
              className="field-input"
              placeholder="Transaction ID / Cheque No."
              value={form.payment_reference}
              onChange={e => setForm({ ...form, payment_reference: e.target.value })}
              data-testid="payment-reference"
            />
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button type="button" onClick={onClose} className="px-5 py-2 border border-slate-300 rounded-sm font-medium hover:bg-slate-50">
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading || form.amount <= 0}
              className="px-5 py-2 bg-green-500 text-white rounded-sm font-bold hover:bg-green-600 disabled:opacity-60"
              data-testid="submit-payment"
            >
              {loading ? "Recording..." : "Record Payment"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
